import { env } from "@/env"
import { clsx, type ClassValue } from "clsx"
import { format } from "date-fns/format"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Get current base (radix or base) from pathname.
export function getCurrentBase(pathname: string): string {
  const baseMatch = pathname.match(/\/docs\/components\/(radix|base)\//)
  return baseMatch ? baseMatch[1] : "radix" // Default to radix.
}

export function absoluteUrl(path: string, isFile = false) {
  if (isFile) {
    return `${env.NEXT_PUBLIC_SERVER_FILE_UPLOAD_URL}/${path}`;
  }
  return `${env.NEXT_PUBLIC_API_BASE_URL}${path}`;
}

export function displayDateFormate(dateStr: string | null | undefined): string {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return "";
  return format(date, "dd MMM, EEE");
}
export function timeSlice(timeString: string | undefined): string {
  if (!timeString) return "";

  // If the time is in HH:MM:SS format, extract HH:MM
  const match = timeString.match(/(\d{1,2}):(\d{2})(?::\d{2})?/);
  if (match) {
    return `${match[1]}:${match[2]}`;
  }

  return timeString;
}
export function minutesToHoursAndMinutes(minutes: number | undefined): {
  time: string;
} {
  if (!minutes) return { time: "0h 0m" };

  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  return {
    time: `${hours}h ${mins}m`,
  };
}
export function formatNumber(num: number | undefined): string {
  if (num === undefined) return "0";
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}
export function formatDuration(minutes?: number): string {
  if (!minutes) return "N/A";
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours === 0) {
    return `${mins}m`;
  }
  return `${hours}h ${mins > 0 ? `${mins}m` : ""}`;
}


/**
 * Generates page numbers for pagination with ellipsis
 * @param currentPage - Current page number (1-based)
 * @param totalPages - Total number of pages
 * @returns Array of page numbers and ellipsis strings
 *
 * Examples:
 * - Small dataset (≤5 pages): [1, 2, 3, 4, 5]
 * - Near beginning: [1, 2, 3, 4, '...', 10]
 * - In middle: [1, '...', 4, 5, 6, '...', 10]
 * - Near end: [1, '...', 7, 8, 9, 10]
 */
export function getPageNumbers(currentPage: number, totalPages: number) {
  const maxVisiblePages = 5 // Maximum number of page buttons to show
  const rangeWithDots = []

  if (totalPages <= maxVisiblePages) {
    // If total pages is 5 or less, show all pages
    for (let i = 1; i <= totalPages; i++) {
      rangeWithDots.push(i)
    }
  } else {
    // Always show first page
    rangeWithDots.push(1)

    if (currentPage <= 3) {
      // Near the beginning: [1] [2] [3] [4] ... [10]
      for (let i = 2; i <= 4; i++) {
        rangeWithDots.push(i)
      }
      rangeWithDots.push('...', totalPages)
    } else if (currentPage >= totalPages - 2) {
      // Near the end: [1] ... [7] [8] [9] [10]
      rangeWithDots.push('...')
      for (let i = totalPages - 3; i <= totalPages; i++) {
        rangeWithDots.push(i)
      }
    } else {
      // In the middle: [1] ... [4] [5] [6] ... [10]
      rangeWithDots.push('...')
      for (let i = currentPage - 1; i <= currentPage + 1; i++) {
        rangeWithDots.push(i)
      }
      rangeWithDots.push('...', totalPages)
    }
  }

  return rangeWithDots
}

export function appendFormDataValues(
  formData: FormData,
  values: Record<string, unknown>,
  parentKey?: string
) {
  Object.entries(values).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;

    const formKey = parentKey ? `${parentKey}[${key}]` : key;

    if (value instanceof File) {
      formData.append(formKey, value);
      return;
    }
    if (value instanceof FileList) {
      Array.from(value).forEach((file) => {
        formData.append(`${formKey}[]`, file);
      });
      return;
    }
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item instanceof File) {
          formData.append(`${formKey}[]`, item);
        } else if (typeof item === "object" && item !== null) {
          appendFormDataValues(
            formData,
            item as Record<string, unknown>,
            `${formKey}[]`
          );
        } else {
          formData.append(`${formKey}[]`, String(item));
        }
      });
      return;
    }
    if (typeof value === "object") {
      appendFormDataValues(
        formData,
        value as Record<string, unknown>,
        formKey
      );
      return;
    }

    formData.append(formKey, String(value));
  });
}
export function appendFormDataValue(
  formData: FormData,
  key: string,
  value: string | number | undefined | null,
) {
  if (value === undefined || value === null) {
    return;
  }

  formData.append(key, String(value));
}


export const formatCurrency = (
  amount?: number | string | null,
  fraction: number = 2,
) => {
  if (!amount) return "0.00";
  const number = Number(amount);

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "BDT",
    minimumFractionDigits: fraction,
  }).format(number);
};


export function rafScrollIntoView(
  el: HTMLElement | null,
  options: ScrollIntoViewOptions = { behavior: "smooth", block: "start" }
) {
  if (!el) return;
  requestAnimationFrame(() => el.scrollIntoView(options));
}

export function getBackendErrorMessage(error: unknown) {
  if (!error || typeof error !== "object") {
    return "Please try again later.";
  }

  const err = error as {
    data?: unknown;
    message?: string;
    error?: string;
  };

  if (typeof err.data === "string" && err.data.trim()) {
    return err.data;
  }

  if (err.data && typeof err.data === "object") {
    const data = err.data as { message?: unknown; error?: unknown };

    if (typeof data.message === "string" && data.message.trim()) {
      return data.message;
    }

    if (typeof data.error === "string" && data.error.trim()) {
      return data.error;
    }
  }

  if (typeof err.message === "string" && err.message.trim()) {
    return err.message;
  }

  if (typeof err.error === "string" && err.error.trim()) {
    return err.error;
  }

  return "Please try again later.";
}
